import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import {
  ArchiveModalItem,
  ArchiveOptionsForModal,
  open as openArchiveModal,
} from 'archive/components/modals/ArchiveModal';
import coreObjectIsArchivable from 'archive/isArchivable';
import isArchived from 'archive/isArchived';
import * as Context from 'Context';
import {simpleToolbarAction, ToolbarAction, ToolbarActionPlacement} from 'ui-library/toolbar/ToolbarAction';

/** A {@link ToolbarAction} annotated with whether it stays visible once the item is archived. */
export interface ArchivableToolbarAction extends ToolbarAction {
  legalWhenArchived: boolean;
}

/**
 * Returns the actions that apply to the item's archival state, as plain {@link ToolbarAction}s: every action
 * while the item is active, and only those marked `legalWhenArchived` once it is archived.
 */
export function hideActionsIllegalWhenArchived(
  actions: Array<ArchivableToolbarAction>,
  isItemArchived: boolean
): Array<ToolbarAction> {
  return actions
    .filter((action) => !isItemArchived || action.legalWhenArchived)
    .map(({key, placement, render}) => ({key, placement, render}));
}

export interface ArchiveToggleActionParams {
  item: ArchiveModalItem;
  /**
   * Where the Archive action renders. Unarchive always renders in the main group, since it is the primary
   * action on an archived item.
   */
  archivePlacement: ToolbarActionPlacement;
  isArchivable: boolean;
  isUnarchivable: boolean;
  onArchive: (itemIds: Array<string>, purpose: string, options: ArchiveOptionsForModal) => void;
  onUnarchive: (itemIds: Array<string>) => void;
  getAnalyticsMetadata?: () => AnalyticsMetadataForContext;
}

/**
 * The toolbar counterpart of `ArchiveOrUnarchiveButton`: Archive (opening the archive modal) for an active
 * item the user may archive, Unarchive for an archived item the user may unarchive, and `null` otherwise.
 */
export function buildArchiveToggleAction(params: ArchiveToggleActionParams): ToolbarAction | null {
  const {item} = params;
  if (!isArchived(item) && coreObjectIsArchivable(item) && params.isArchivable) {
    return simpleToolbarAction({
      key: 'archive',
      name: 'Archive',
      icon: 'archive',
      placement: params.archivePlacement,
      destructive: true,
      dataTestAttrs: {component: 'ArchiveToolbarAction', element: 'archive'},
      onSelect: () => {
        openArchiveModal({
          items: [item],
          onArchive: params.onArchive,
          analyticsMetadata: params.getAnalyticsMetadata?.(),
        });
      },
    });
  }
  if (isArchived(item) && params.isUnarchivable) {
    return simpleToolbarAction({
      key: 'unarchive',
      name: 'Unarchive',
      icon: 'unarchive',
      placement: 'main',
      dataTestAttrs: {component: 'ArchiveToolbarAction', element: 'unarchive'},
      onSelect: () => {
        Context.using({analyticsMetadata: params.getAnalyticsMetadata?.()}, null, () => {
          params.onUnarchive([item.id]);
        });
      },
    });
  }
  return null;
}
